import BioForm from "@features/onboarding/components/BioForm";
import DateOfBirthForm from "@features/onboarding/components/DateOfBirthForm";
import DesiredGendersForm from "@features/onboarding/components/DesiredGendersForm";
import DisplayNameForm from "@features/onboarding/components/DisplayNameForm";
import GenderForm from "@features/onboarding/components/GenderForm";
import OnboardingAppBar from "@features/onboarding/components/OnboardingAppBar";
import PhotoUploadForm from "@features/onboarding/components/PhotoUploadForm";
import useOnboardingField from "@features/onboarding/hooks/useOnboardingField";
import { ONBOARDING_FIELDS, type OnboardingField } from "@fitties/shared";
import logger from "@logger";
import { useCurrentUser } from "@shared/queries";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

interface OnboardingFormProps {
  /** Called after the step's data is saved successfully. */
  onSaved?: () => void;
}

const FORM_COMPONENTS: Partial<Record<OnboardingField, React.FC<OnboardingFormProps>>> = {
  display_name: DisplayNameForm,
  bio: BioForm,
  date_of_birth: DateOfBirthForm,
  gender: GenderForm,
  desired_genders: DesiredGendersForm,
  photo_url: PhotoUploadForm,
} as const;

/**
 * Main onboarding page component.
 *
 * Manages step navigation state, allowing users to go back to
 * previously completed steps via the app bar back button. The
 * active step syncs forward automatically when a mutation advances
 * the derived step.
 */
export default function OnboardingPage() {
  const { user } = useCurrentUser();
  const { currentFieldIndex } = useOnboardingField();

  const [activeStepIndex, setActiveStepIndex] = useState(currentFieldIndex);
  const prevFieldIndexRef = useRef(currentFieldIndex);
  const frontierIndexRef = useRef(currentFieldIndex);

  useEffect(() => {
    frontierIndexRef.current = currentFieldIndex;
    if (currentFieldIndex !== prevFieldIndexRef.current) {
      setActiveStepIndex(currentFieldIndex);
      prevFieldIndexRef.current = currentFieldIndex;
    }
  }, [currentFieldIndex]);

  const activeField = ONBOARDING_FIELDS[activeStepIndex] ?? null;
  const canGoBack = activeStepIndex > 0;
  const goBack = () => setActiveStepIndex((i) => Math.max(0, i - 1));

  // Advance to the next step after a successful save. Re-saving an
  // already-completed (earlier) step does not move the derived frontier,
  // so we advance explicitly here, clamped so we never skip ahead of the
  // first incomplete field.
  const handleStepSaved = () => {
    setActiveStepIndex((i) => Math.min(i + 1, frontierIndexRef.current));
  };

  logger.debug("🔍 OnboardingPage Debug Info:", {
    user,
    activeField,
    activeStepIndex,
    currentFieldIndex,
  });

  const renderStepContent = () => {
    if (!activeField) {
      return null;
    }

    const FormComponent = FORM_COMPONENTS[activeField];
    if (!FormComponent) {
      // Guard clause: This should never happen
      return null;
    }

    // Render the step component
    return <FormComponent onSaved={handleStepSaved} />;
  };

  return (
    <div data-testid="onboarding-page" className={clsx("h-screen flex flex-col", "bg-lavender-400")}>
      <OnboardingAppBar canGoBack={canGoBack} onBack={goBack} activeStepIndex={activeStepIndex} />

      <main data-testid="onboarding-step-content" className="flex-1 min-h-0 flex items-center justify-center px-6 py-4">
        {renderStepContent()}
      </main>
    </div>
  );
}
